import { useCallback, useContext, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { message } from 'antd';
import useSwr from '@hooks/useSwr';
import SessionContext from '@providers/Session/SessionContext';
import api from '@services/api';
import { useNotificationSound } from '@hooks/useNotificationSound';

interface ToastNotificationItem {
  disparo: string;
  idAlarme: string;
  nomeAlarme: string;
  quantidadeDisparo: string;
  tipoAlarme: string;
  tipoAlarmeOriginal: string | null;
  tipoDisparo: string;
  valorDisparo: string;
  nomeGrafico: string;
  timeId: string;
}

export const useNotificationToast = () => {
  const { t } = useTranslation(['main', 'alarms-notifications']);
  const navigate = useNavigate();
  const { customersSwitch } = useContext(SessionContext);

  const [removedToastNotifications, setRemovedToastNotifications] = useState<
    Set<string>
  >(new Set());
  const [loadingToastNotifications, setLoadingToastNotifications] = useState<
    Set<string>
  >(new Set());

  const [displayedToastNotifications, setDisplayedToastNotifications] =
    useState<Set<string>>(new Set());

  const {
    data: toastNotifications,
    isLoading: isToastNotificationsLoading,
    mutate: mutateToastNotifications,
  } = useSwr(
    '/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/listar/notificacao_toast',
    {
      headers: {
        customerId: customersSwitch?.selected,
      },
    },
    {
      refreshInterval: 300000,
      revalidateOnFocus: true,
      revalidateOnReconnect: true,
      dedupingInterval: 60000,
    },
  );

  const filteredToastNotifications = useMemo(() => {
    if (!toastNotifications) return [];
    return toastNotifications.filter(
      (notification: ToastNotificationItem) =>
        !removedToastNotifications.has(notification.idAlarme),
    );
  }, [toastNotifications, removedToastNotifications]);

  const newToastNotifications = useMemo(() => {
    return filteredToastNotifications.filter(
      (notification: ToastNotificationItem) =>
        !displayedToastNotifications.has(notification.idAlarme),
    );
  }, [filteredToastNotifications, displayedToastNotifications]);

  const { previousNotificationCount } = useNotificationSound(
    filteredToastNotifications,
  );

  const handleRemoveToastNotification = useCallback(
    async (idAlarme: string, timeId: string) => {
      setLoadingToastNotifications(prev => new Set(prev).add(idAlarme));

      try {
        await api.patch(
          `/service-proxy/dashboard/simcard_online/alarme/kapacitor/alerta_task/atualiza_toast`,
          {
            idTask: idAlarme,
            timeId,
          },
          {
            headers: {
              customerId: customersSwitch?.selected,
            },
          },
        );

        setRemovedToastNotifications(prev => new Set(prev).add(idAlarme));
        setDisplayedToastNotifications(prev => {
          const newSet = new Set(prev);
          newSet.delete(idAlarme);
          return newSet;
        });
        mutateToastNotifications();
      } catch (error) {
        message.error(t('alarms-notifications:removeNotificationError'));
      } finally {
        setLoadingToastNotifications(prev => {
          const newSet = new Set(prev);
          newSet.delete(idAlarme);
          return newSet;
        });
      }
    },
    [customersSwitch?.selected, mutateToastNotifications, t],
  );

  const handleToastNotificationClick = useCallback(
    (notification: ToastNotificationItem, onClose?: () => void) => {
      if (onClose) {
        onClose();
      }

      const params: Record<string, string> = {
        nomeAlarme: notification.nomeAlarme,
        level: notification.disparo,
        nomeGrafico: notification.nomeGrafico,
        page: '1',
      };

      // Só adiciona tipoAlarme se não estiver usando tipoAlarmeOriginal
      if (!notification.tipoAlarmeOriginal) {
        params.tipoAlarme = notification.tipoAlarme;
      }

      const searchParams = new URLSearchParams(params);

      navigate(
        `/indicators/alarms/page-alarms-notification?${searchParams.toString()}`,
      );
    },
    [navigate],
  );

  const markToastAsDisplayed = useCallback((idAlarme: string) => {
    setDisplayedToastNotifications(prev => new Set(prev).add(idAlarme));
  }, []);

  const formatAlarmType = useCallback(
    (alarm: string, alarmOriginal?: string | null) => {
      const displayAlarm = alarmOriginal || alarm;

      if (displayAlarm === 'threshold') {
        return t('alarms-notifications:threshold');
      }
      if (displayAlarm === 'variacao_percentual') {
        return t('alarms-notifications:percentualVariation');
      }
      if (displayAlarm === 'sazonal') {
        return t('alarms-notifications:seasonal');
      }

      return '';
    },
    [t],
  );

  return {
    toastNotifications,
    filteredToastNotifications,
    newToastNotifications,
    isToastNotificationsLoading,
    removedToastNotifications,
    loadingToastNotifications,
    handleRemoveToastNotification,
    handleToastNotificationClick,
    formatAlarmType,
    mutateToastNotifications,
    previousNotificationCount,
    markToastAsDisplayed,
  };
};

export type { ToastNotificationItem };
